import React, { useState, useEffect } from "react";
import {
  Paper,
  Button,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import styles from "../../Dashboard.module.css";
import useGroupsApi from "../../../../hooks/useGroupsApi";
import useNotification from "../../../../hooks/useNotification";
import NotificationSnackbar from "../../../../components/NotificationSnackbar.jsx";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import { useRoleNavigator } from "../../../../utils/roleNavigator.js";
import SearchIcon from "@mui/icons-material/Search";
import { Cancel } from "@mui/icons-material";
import { sanitizeInput } from "../../../../utils/inputSanitizer.js";

export default function GroupsPage() {
  const notificationApi = useNotification();
  const { notification, hideNotification, showNotification } = notificationApi;
  const { loading, groups, fetchGroups, createGroup } =
    useGroupsApi(showNotification);

  // State
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Create Group Modal State
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    filterGroups();
  }, [groups, searchTerm]);

  const loadGroups = async () => {
    await fetchGroups();
  };

  const filterGroups = () => {
    let filtered = groups;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((group) =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    console.log("Filtered Groups:", filtered);
    setFilteredGroups(filtered);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCreateGroup = async () => {
    setValidationErrors({});

    if (!newGroup.name.trim()) {
      setValidationErrors({ name: "Group name is required" });
      return;
    }

    setCreateLoading(true);
    try {
      const result = await createGroup(newGroup);

      if (result.success) {
        setCreateModalOpen(false);
        setNewGroup({ name: "" });
        await loadGroups(); // Refresh the groups list
      }
    } finally {
      setCreateLoading(false);
    }
    fetchGroups();
  };
  const navigateByRole = useRoleNavigator();
  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 250,
    },
    {
      field: "operatorCount",
      headerName: "# Operators",
      width: 150,
      renderCell: (params) => (params.value === 0 ? "-" : params.value),
    },
    {
      field: "actions",
      headerName: "Edit",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <IconButton
          style={{ color: "var(--color-icon)" }}
          title="Edit"
          onClick={
            //  () => navigate(`/superadmin/groups/${params.row.id}`)
            () => navigateByRole(`groups/${params.row.id}`)
          }
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  // Ensure each row has a unique ID
  const rows = filteredGroups.map((group) => ({
    id: group.id,
    ...group,
  }));

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 className={styles.dashboardTitle}>GROUPS</h1>
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            minWidth: 0,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              position: "relative",
              minWidth: 0,
              flex: "1 1 120px",
              width: "100%",
            }}
          >
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              style={{
                padding: "8px 24px 8px 40px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: "0.98rem",
                width: "100%",
                height: 40,
                boxSizing: "border-box",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "55%",
                transform: "translateY(-50%)",
                color: "#888",
                pointerEvents: "none",
              }}
            >
              <SearchIcon />
            </span>
          </div>
          <div style={{ position: "relative", display: "inline-block" }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              onClick={() => setCreateModalOpen(true)}
              sx={{
                width: "100%",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-secondary)" },
              }}
            >
              Add Group
            </Button>
          </div>
        </div>
      </div>

      <Paper sx={{ height: 580, width: "100%", borderRadius: 2, boxShadow: 2, cursor: 'pointer'  }}>
        <DataGrid
          rows={rows}
          onRowDoubleClick={(params) => navigateByRole(`groups/${params.row.id}`)}  
          
          columns={columns}
          rowHeight={36}
          loading={loading}
          pageSizeOptions={[15, 30, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 15, page: 0 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "14px",
              fontWeight: "bold",
            },
          }}
          localeText={{
            noRowsLabel: "No groups found",
            loadingOverlayLabel: "Loading...",
          }}
        />
      </Paper>

      <Dialog
        open={createModalOpen}
        onClose={() => (setCreateModalOpen(false), setNewGroup({ name: "" }))}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: 300,
            maxWidth: 350,
            p: 1,
            position: "relative",
          },
        }}
      >
        {/* Absolute positioned close button */}
        <IconButton
          aria-label="close"
          onClick={() => (setCreateModalOpen(false), setNewGroup({ name: "" }))}
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "var(--color-close-icon)",
            zIndex: 1,
          }}
        >
          <Cancel fontSize="small" />
        </IconButton>
        <DialogTitle sx={{ pr: 4 }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            color="var(--color-primary)"
          >
            {"Add a New Group".toUpperCase()}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Group Name"
            value={newGroup.name}
            onChange={(e) =>
              setNewGroup({ ...newGroup, name: sanitizeInput(e.target.value) })
            }
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            required
            autoFocus
            sx={{ mt: 2, mb: 2 }}
          />

          <Button
            fullWidth
            onClick={handleCreateGroup}
            disabled={createLoading}
            variant="contained"
            sx={{
              backgroundColor: "var(--color-primary)",
              "&:hover": { backgroundColor: "var(--color-secondary)" },
            }}
          >
            {createLoading ? "Creating..." : "Add Group"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
